/**
 * Per-module "Ask the Detective" prompts.
 *
 * GROUNDING RULE — read before adding a question.
 * The Detective refuses anything its retriever cannot ground. The RAG corpus
 * indexes rule definitions (every rule_id, name and description), the glossary,
 * NL→SQL exemplars and schema docs. So:
 *
 *   GOOD  "What does rule OD-001 Odometer Rollback detect?"   (rule definition)
 *   GOOD  "Why does a round oil amount matter?"               (glossary/description)
 *   BAD   "How many alerts fired today?"                      (live count → refused)
 *
 * Every rule id below is verified to have a catalog entry in
 * `services/llm/context/rules/<rule_id>.yaml` — that file IS the indexed
 * chunk, so an id without one is not reliably groundable (TM-005 and DQ-1..4
 * have no YAML, which is why they are absent here). Grep before adding.
 *
 * The YAML carries no `name` field, so rule names are spelled out in the
 * question text to give the retriever a lexical hook from the docs corpus as
 * well as an embedding hook from the YAML.
 */
export const DETECTIVE_CHIPS: Record<string, string[]> = {
  overview: [
    'What does rule PR-004 Inflated Transaction detect?',
    'What does rule VOL-001 Tank Overflow detect?',
  ],
  'offender-vehicles': [
    'What does rule OD-001 Odometer Rollback detect?',
    'What does rule OD-003 Odometer Jump detect?',
  ],
  'fraud-map': [
    'What does rule OIL-1 detect?',
    'Why does a round oil amount matter?',
    'What does rule TOLL-4 Toll-Only Day detect?',
  ],
  'fraud-dashboard': [
    // A severity question would only ground on docs/demo/qa/02-fraud-rules.md,
    // which is not present at this branch's base commit. Asked against a rule
    // instead: OIL-2.yaml carries both `severity:` and the description, and
    // sump capacity is make/model-derived, which ties to the by-make chart.
    'What does rule OIL-2 Excess Oil vs Sump Capacity detect, and how severe is it?',
    'What does rule KE-003 KML Trend Decline detect?',
  ],
  'fraud-center': [
    'What does rule TM-004 Rapid Succession detect?',
    'What does rule CD-005 Card Velocity detect?',
  ],
  'rule-health': [
    'What does rule CP-001 Above Baseline detect?',
    'What does rule KE-001 KML Below Baseline detect?',
  ],
  consumption: [
    'What does rule KE-003 KML Trend Decline detect?',
    'What does L/100km baseline drift mean?',
  ],
  'merchant-network': [
    'What does rule OIL-3 Orphaned Oil Transaction detect?',
  ],
  maps: ['What does rule TM-001 After Hours Fueling detect?'],
  // "alert confidence" has zero hits in the indexed corpus, so the DQ chip is
  // a rule question instead: OD-005's own business_meaning explains why a
  // missing reading silently disables the other odometer rules.
  'data-quality': ['What does rule OD-005 Zero Odometer detect?'],
};

/** Questions for `module`, or an empty array when nothing is configured. */
export function getDetectiveChips(module: string): string[] {
  return DETECTIVE_CHIPS[module] ?? [];
}
